import { dbRefsWithRelationshipKeys } from '@caresend/types';
import dayjs from 'dayjs';
import cloneDeep from 'lodash.clonedeep';
import { getUnknownErrorMessage } from '../error/errorHandling.js';
import { generateID } from '../initializers/id.js';
import 'immutability-helper';
import 'lodash-unified';
import { objectMap, objectKeys } from '../languageExtensions/object.js';
import { nullishFilter, isNullish, removeUndefinedValuesFromObj } from '../typeValidation/nullish.js';
import { isObject } from '../typeValidation/basicGuards.js';
import '../typeValidation/booking.js';
import '../typeValidation/draftWaypoint.js';
import '../typeValidation/feedback.js';
import '../formatters/string.js';
import 'ngeohash';
import '../environment/crypto/index.js';
import '../formatters/location.js';
import '../initializers/user.js';
import '../typeValidation/note.js';
import '../typeValidation/procedure.js';
import '../typeValidation/samples.js';
import '../typeValidation/shift.js';
import '../typeValidation/shipmentInfo.js';
import '../typeValidation/supply.js';
import '../typeValidation/timeSlot.js';
import '../typeValidation/waypoint.js';
import '../typeValidation/waypointAction.js';

/* eslint-disable @typescript-eslint/no-non-null-assertion */
/**
 * Some keys are excluded from `recursiveDecrypt` because they currently are
 * listed as encrypted in the unencrypted types.
 *
 * For example, both `User` and `Procedure` have `diagnoses.icd10Code` typed as
 * `EncryptedData`. The `icd10Code` key can be removed once we set these types
 * to only be encrypted on `EncryptedUser` and `EncryptedProcedure`.
 *
 * Note: recursiveEncryption is only set up for return type when not binding
 * (typically cloud-side). Decryption status is unknown on the frontend.
 *
 * Note: `wallet` is not encrypted on `User` but is on `Office`, so it has been
 * excluded here, and will be decrypted in the FWR return value on offices.
 */

const noDecryptKeys = ['icd10Code', 'secretID'];

const isEncrypted = value => (value === null || value === void 0 ? void 0 : value.encrypted) !== undefined;
/** Same as `isObject` but with a different type guard */


const isRecord = value => isObject(value);

const isArray = value => Array.isArray(value);

const isString = value => typeof value === 'string';

const isIdObj = object => (object === null || object === void 0 ? void 0 : object.id) !== undefined;

const isIdObjOrStringArray = array => Array.isArray(array) && array.every(item => isString(item) || isIdObj(item));

let config = null;
/**
 * Configures the fetchWithRelationships function
 *
 * Cloud-side, this must be called before any invocation and provide the functions
 * that will be used to fetch data and decrypt it. Client-side, this will be called
 * automatically once a user is logged in.
 */

const initFetchWithRelationshipsConfig = ({
  bind,
  unbind,
  loggingMeta,
  getValueOnce,
  decryptData,
  encryptionCacheService
}) => {
  if (loggingMeta === null || loggingMeta === void 0 ? void 0 : loggingMeta.globalVerbose) {
    console.info('[FWR verbose]: Verbose logging enabled globally');
  }

  config = {
    bind,
    unbind,
    loggingMeta,
    getValueOnce,
    decryptData,
    encryptionCacheService
  };
};
/**
 * Sets metadata related to verbose logging. Used in apps to set the data
 * after variables are loaded.
 */

const setFWRLoggingMeta = loggingMeta => {
  if (!config) {
    console.error('[FWR]: setFWRLoggingMeta called before initFetchWithRelationshipsConfig');
    return;
  }

  if (loggingMeta === null || loggingMeta === void 0 ? void 0 : loggingMeta.globalVerbose) {
    console.info('[FWR verbose]: Verbose logging enabled globally');
  }

  config.loggingMeta = loggingMeta;
};
/**
 * @deprecated - Marked deprecated for now due to extreme slowdowns in cloud
 * function performance for large fetch requests. Was previously included
 * after the `getValueOnce` call. Improve performance or remove.
 *
 * Decrypts all possible child data unless the key is in `noDecryptKeys`. Used
 * only when not in bind mode (typically cloud-side).
 */

const recursiveDecrypt = async item => {
  if (!config || !(isObject(item) || isArray(item))) return item;

  if (isEncrypted(item)) {
    const decrypted = await config.decryptData(item);
    return decrypted;
  }

  if (isArray(item)) {
    const decryptedItemPromises = item.map(recursiveDecrypt);
    const decryptedItems = await Promise.all(decryptedItemPromises);
    return decryptedItems;
  }

  if (isObject(item)) {
    const entries = Object.entries(item);
    const decryptedEntryPromises = entries.map(async ([key, value]) => {
      if (noDecryptKeys.includes(key)) return [key, value];
      const decrypted = await recursiveDecrypt(value);
      return [key, decrypted];
    });
    const decryptedEntries = await Promise.all(decryptedEntryPromises);
    return Object.fromEntries(decryptedEntries);
  }

  return item;
};
/**
 * Returns the IDs of the child items to be fetched
 */

const getChildFetchIDs = async (parent, childKey, setLoadingState) => {
  var _a;

  let childIDs = [];
  const item = parent;
  if (!item || !config) return {
    childIDs
  };
  let itemChild = parent[childKey];
  let wasDecrypted = false;

  if (isEncrypted(itemChild)) {
    let decrypted = (_a = config.encryptionCacheService) === null || _a === void 0 ? void 0 : _a.call(config, itemChild.id);

    if (!decrypted) {
      setLoadingState(true, itemChild.id);
      decrypted = await config.decryptData(itemChild);
      if (!decrypted || !item) return {
        childIDs
      };
      setLoadingState(false, itemChild.id);
    }

    itemChild = cloneDeep(decrypted);
    item[childKey] = cloneDeep(decrypted);
    wasDecrypted = true;
  }

  if (isString(itemChild)) {
    return {
      childIDs: [itemChild],
      itemChild,
      wasDecrypted
    };
  }

  if (isIdObjOrStringArray(itemChild)) {
    childIDs = itemChild.map(childItem => {
      if (isIdObj(childItem)) {
        return childItem.id;
      }

      return childItem;
    });
    return {
      childIDs,
      itemChild,
      wasDecrypted
    };
  }

  if (isRecord(itemChild)) {
    const recordPromises = Object.entries(itemChild).map(async ([childID, childItem]) => {
      if (!item || !config) return [childID, childItem];

      if (isIdObj(childItem)) {
        return [childItem.id, childItem];
      }

      if (isEncrypted(childItem)) {
        setLoadingState(true, childItem.id);
        const decrypted = await config.decryptData(childItem);
        setLoadingState(false, childItem.id);

        if (isString(decrypted)) {
          return [decrypted, true];
        }

        if (isIdObj(decrypted)) {
          return [decrypted.id, decrypted];
        }

        wasDecrypted = true;
      }

      return [childID, childItem];
    });
    const entries = (await Promise.all(recordPromises)).filter(nullishFilter);
    childIDs = entries.filter(nullishFilter).map(([id, _item]) => id);
    return {
      childIDs,
      itemChild: Object.fromEntries(entries),
      wasDecrypted
    };
  }

  return {
    childIDs,
    itemChild,
    wasDecrypted
  };
};
/** DO NOT USE: Internal value for recursion */


const accumulators = {};
const accumulatorsTimestamps = {};
const activeBindingsByPath = {};
const writeCallbacksByContext = {};
/**
 * Batches are organized by batchContextID
 * This allows a single writer callback to be used for multiple batches
 * without having to define multiple writer callbacks
 */

const batches = {};
const batchesInfo = {};
/**
 * To be called in the root execution of `fetchWithRelationships` only.
 */

const initAccumulator = (ref, entityID) => {
  /**
   * Generate a unique ID for the accumulator so that calls to
   * `fetchWithRelationships` with the same `ref` and `entityID` will act
   * separately. `bind` calls will still be grouped by path. Write callbacks
   * will be grouped by `inputBatchingContextID` if provided.
   */
  const accumulatorID = `${ref}/${entityID}-${generateID(6)}`;
  accumulators[accumulatorID] = {};
  accumulatorsTimestamps[accumulatorID] = Date.now();
  return accumulatorID;
};
/**
 * To be called in the root execution of `fetchWithRelationships` only.
 */


const initBatchingContext = (accumulatorID, batchingContextID, writers) => {
  const batchingContext = batchingContextID !== null && batchingContextID !== void 0 ? batchingContextID : accumulatorID;

  if (writers) {
    writeCallbacksByContext[batchingContext] = writers;
  }

  return batchingContext;
};

const cleanupBatches = contextID => {
  delete batches[contextID];
  delete batchesInfo[contextID];
};

const cleanupAccumulators = accID => {
  delete accumulators[accID];
  delete accumulatorsTimestamps[accID];
};

const cleanup = (accID, contextID, prevAccumulatorID) => {
  var _a;

  if (!prevAccumulatorID) {
    cleanupBatches(contextID);
  }

  if (!Object.keys((_a = batches[contextID]) !== null && _a !== void 0 ? _a : {}).length) {
    cleanupAccumulators(accID);
  }
};

const getResponseAndCleanup = (accID, contextID, prevAccumulatorID) => {
  var _a;

  const response = (_a = accumulators[accID]) !== null && _a !== void 0 ? _a : {};
  cleanup(accID, contextID, prevAccumulatorID);
  return response;
};

const getBindingsReport = () => {
  const generated = dayjs().format('MM/DD/YYYY hh:mm:ss a');
  return {
    generated,
    writeCallbacksByContext,
    activeBindingsByPath
  };
};
const logBindings = () => {
  const bindingsCount = Object.values(activeBindingsByPath).length;
  const allActiveBindings = Object.values(activeBindingsByPath);
  const bindingsByContext = allActiveBindings.reduce((acc, binding) => {
    Object.keys(binding.relationshipsByContext || {}).forEach(context => {
      acc[context] = (acc[context] || 0) + 1;
    });
    return acc;
  }, {});
  const bindingsWithTotal = Object.assign(Object.assign({}, bindingsByContext), {
    total: bindingsCount
  });
  console.table({
    'Current bindings by context': bindingsWithTotal
  });
};
/**
 * Internal function for batching writes back to the input callback functions
 * This waits some defined number of milliseconds before writing back to the callback
 * to allow for the batching of multiple writes together
 */

const batchedWriter = (inputParams, logVerbose) => new Promise((resolve, _reject) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;

  const {
    accumulatorID,
    batchingContextID,
    bindResponse,
    id,
    newItem,
    ref,
    timeout,
    writers
  } = inputParams;
  if (isNullish(newItem)) return resolve();

  if ((_b = (_a = batches[batchingContextID]) === null || _a === void 0 ? void 0 : _a[ref]) === null || _b === void 0 ? void 0 : _b.timeout) {
    batches[batchingContextID][ref].resolves = [...((_c = batches[batchingContextID][ref].resolves) !== null && _c !== void 0 ? _c : []), resolve];
    const batchTimeout = (_e = (_d = batches[batchingContextID]) === null || _d === void 0 ? void 0 : _d[ref]) === null || _e === void 0 ? void 0 : _e.timeout;
    if (batchTimeout) clearTimeout(batchTimeout);
  }

  batches[batchingContextID] = Object.assign(Object.assign({}, batches[batchingContextID]), {
    [ref]: Object.assign(Object.assign({}, (_f = batches[batchingContextID]) === null || _f === void 0 ? void 0 : _f[ref]), {
      timeout: setTimeout(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;

        const batch = cloneDeep((_c = (_b = (_a = batches[batchingContextID]) === null || _a === void 0 ? void 0 : _a[ref]) === null || _b === void 0 ? void 0 : _b.response) !== null && _c !== void 0 ? _c : {});
        logVerbose(`Writing batch for ${ref}`);
        (_d = writers === null || writers === void 0 ? void 0 : writers[ref]) === null || _d === void 0 ? void 0 : _d.call(writers, batch, (_e = accumulators[accumulatorID !== null && accumulatorID !== void 0 ? accumulatorID : '']) !== null && _e !== void 0 ? _e : {}, bindResponse, ref);
        batchesInfo[batchingContextID] = Object.assign(Object.assign({}, batchesInfo[batchingContextID]), {
          [ref]: [...((_g = (_f = batchesInfo[batchingContextID]) === null || _f === void 0 ? void 0 : _f[ref]) !== null && _g !== void 0 ? _g : []), {
            batchCount: Object.keys((_k = (_j = (_h = batches[batchingContextID]) === null || _h === void 0 ? void 0 : _h[ref]) === null || _j === void 0 ? void 0 : _j.response) !== null && _k !== void 0 ? _k : {}).length,
            batchTimestamps: Date.now()
          }]
        });
        (_o = (_m = (_l = batches[batchingContextID]) === null || _l === void 0 ? void 0 : _l[ref]) === null || _m === void 0 ? void 0 : _m.resolves) === null || _o === void 0 ? void 0 : _o.forEach(res => res());
        (_p = batches[batchingContextID]) === null || _p === void 0 ? true : delete _p[ref];
      }, timeout),
      response: Object.assign(Object.assign({}, (_h = (_g = batches[batchingContextID]) === null || _g === void 0 ? void 0 : _g[ref]) === null || _h === void 0 ? void 0 : _h.response), {
        [id]: cloneDeep(newItem)
      })
    })
  });
});

const maybeHydrateItemFromCache = (item, relationships, logVerbose) => {
  if (!(config === null || config === void 0 ? void 0 : config.encryptionCacheService) || !item) return item;

  const isItemKey = k => k in item;

  objectKeys(relationships).forEach(key => {
    var _a;

    if (!isItemKey(key)) return;
    const childItem = item[key];
    if (!isEncrypted(childItem)) return;
    const cached = (_a = config === null || config === void 0 ? void 0 : config.encryptionCacheService) === null || _a === void 0 ? void 0 : _a.call(config, childItem.id);
    if (!cached) return;
    logVerbose(`Hydrating encrypted item from cache: ${childItem.id} (${String(key)})`);
    item[key] = cached;
  });
  return item;
};

const getDebugInfo = (ref, entityID, options = {}, privateOptions = {}) => {
  var _a, _b;

  return removeUndefinedValuesFromObj({
    bind: options.bind,
    depth: (_a = privateOptions.depth) !== null && _a !== void 0 ? _a : 0,
    path: `${ref}/${entityID}`,
    parent: privateOptions.parentPath,
    context: options.batchingContextID,
    accumulatingFor: (_b = privateOptions.prevAccumulatorID) !== null && _b !== void 0 ? _b : 'none'
  });
};
/**
 * Returns a functions that accepts error/string arguments. The resulting
 * function returns an `Error` than can be thrown, of the resulting function
 * is called without `throw`, it will only log the error.
 */


const getFWRError = (ref, entityID, options = {}, privateOptions = {}) => (...args) => {
  const debugInfo = getDebugInfo(ref, entityID, options, privateOptions);
  const stringArgs = args.map(arg => typeof arg !== 'string' ? getUnknownErrorMessage(arg) : arg); // eslint-disable-next-line no-console

  console.warn('[FWR]:', ...stringArgs, debugInfo);
  return Error(...stringArgs);
};
/**
 * Verbose logging can be enabled as follows:
 * - Pass `options.verbose` as `true` to a single `fetchWithRelationships` call
 * - Apps - global - Enable the feature flag `FRONTEND_VERBOSE_FWR`
 * - Cloud - global - Set `CloudFetchWithRelationshipsMeta.globalVerbose`
 * - Cloud - per-ref - Add the root ref to
 *   `CloudFetchWithRelationshipsMeta.verboseRefs`
 */


const getLogVerbose = ({
  accumulatorID,
  entityID,
  options,
  privateOptions,
  ref
}) => {
  var _a, _b, _c, _d;

  const globalVerbose = (_a = config === null || config === void 0 ? void 0 : config.loggingMeta) === null || _a === void 0 ? void 0 : _a.globalVerbose;
  const rootRef = (_b = privateOptions.rootRef) !== null && _b !== void 0 ? _b : ref;
  const refVerbose = (_d = (_c = config === null || config === void 0 ? void 0 : config.loggingMeta) === null || _c === void 0 ? void 0 : _c.verboseRefs) === null || _d === void 0 ? void 0 : _d.includes(rootRef); // Return a noop if verbose is not enabled

  if (!globalVerbose && !refVerbose && !options.verbose) return () => {};
  return (message, additionalInfo) => {
    var _a;

    const debugInfo = Object.assign(Object.assign({}, getDebugInfo(ref, entityID, options, privateOptions)), additionalInfo && {
      additionalInfo
    });
    const depthIndicator = '░'.repeat((_a = debugInfo.depth) !== null && _a !== void 0 ? _a : 0); // `console.debug` allows for filtering out these heavy logs in the browser
    // and Google Cloud.
    // eslint-disable-next-line no-console

    console.debug('[FWR verbose]:', accumulatorID, depthIndicator, message, debugInfo);
  };
};

const initCallbackHelpers = helperParams => {
  const {
    accumulatorID,
    batchingContextID,
    entityID,
    options,
    privateOptions,
    ref
  } = helperParams;
  const {
    writers
  } = options;
  const {
    batchingTimeout = 50
  } = privateOptions;
  const logVerbose = getLogVerbose(helperParams);
  return {
    setLoadingState: (loading, id = entityID) => batchedWriter({
      accumulatorID,
      batchingContextID,
      bindResponse: true,
      id,
      newItem: loading,
      ref: 'loadingIDs',
      timeout: batchingTimeout,
      writers
    }, logVerbose),

    /**
     * Replace the writer callback with the active binding's writer. This is to
     * ensure the writer is sourced from memory when executed as a result of a
     * Firebase bind response.
     */
    writerCallback: (data, bindResponse) => batchedWriter({
      accumulatorID,
      batchingContextID,
      bindResponse,
      id: entityID,
      newItem: data,
      ref,
      timeout: batchingTimeout,
      writers
    }, logVerbose)
  };
};
/**
 * Returns a Promise of an object with the item requested bundled with child and sibling
 * relationship items structured as a subset of our NoSQL architecture.
 *
 * @param ref The DbRef of the item being fetched. Strongly typed
 * to restrict the entity to the defined DbRefs type.
 * @param entityID The ID of the item being fetched
 * @param fetchRelationships The relationships of the item being fetched. Structure
 * should match the RelationshipKey type which is a subset of each object type.
 * @param options {@link FetchWithRelationshipsOptions}
 * @param privateOptions {@link FetchWithRelationshipsPrivateOptions} Only used
 * intenally
 */


const fetchWithRelationships = async (ref, entityID, fetchRelationships, options = {}, privateOptions = {}) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;

  const {
    bind = false,
    writers
  } = options;
  const {
    batchingTimeout = 50,
    parentPath,
    prevAccumulatorID
  } = privateOptions;
  const depth = (_a = privateOptions.depth) !== null && _a !== void 0 ? _a : 0;
  const rootRef = (_b = privateOptions.rootRef) !== null && _b !== void 0 ? _b : ref;
  const {
    isCloud
  } = (_c = config === null || config === void 0 ? void 0 : config.loggingMeta) !== null && _c !== void 0 ? _c : {};
  const isRootCall = !prevAccumulatorID;
  const FWRError = getFWRError(ref, entityID, options, privateOptions);
  if (!config) throw FWRError('Config not initialized');
  const accumulatorID = isRootCall ? initAccumulator(ref, entityID) : prevAccumulatorID;
  const batchingContextID = isRootCall ? initBatchingContext(accumulatorID, options.batchingContextID, writers) : (_d = options.batchingContextID) !== null && _d !== void 0 ? _d : accumulatorID;
  const helperParams = {
    accumulatorID,
    batchingContextID,
    entityID,
    options,
    privateOptions,
    ref
  };
  const logVerbose = getLogVerbose(helperParams);

  if (isRootCall) {
    if (isCloud) {
      // Always log start of FWR calls in cloud to help identify runs that
      // never complete
      console.info('[FWR]:', 'Starting', accumulatorID, {
        fetchRelationships
      });
    }

    logVerbose('Initializing root call', {
      fetchRelationships
    });
  } else {
    logVerbose(`Initializing child call (${ref})`);
  }

  let item = null;

  if (!entityID) {
    console.info('No entity ID provided', ref);
    return (_e = accumulators[accumulatorID]) !== null && _e !== void 0 ? _e : {};
  }

  const path = `${ref}/${entityID}`;
  const activeBindIsInitialized = ((_f = activeBindingsByPath[path]) === null || _f === void 0 ? void 0 : _f.invoked) !== undefined;
  /** Callback function helpers for batching */

  const cb = initCallbackHelpers(helperParams);
  const writingCallbacks = writers || writeCallbacksByContext[batchingContextID];

  try {
    if (bind && !config.bind) {
      throw FWRError('Binding is not supported cloud-side');
    }

    if (bind && !writingCallbacks) {
      throw FWRError('Binding requires a writer callback');
    }

    if (bind && config.bind && writingCallbacks && !activeBindIsInitialized) {
      /**
       * Block the binding from running again. This must occur on the same runtime tick as separate
       * invocations of the same context & path to avoid duplicate bindings.
       */
      activeBindingsByPath[path] = Object.assign(Object.assign({}, activeBindingsByPath[path]), {
        relationshipsByContext: Object.assign(Object.assign({}, (_g = activeBindingsByPath[path]) === null || _g === void 0 ? void 0 : _g.relationshipsByContext), {
          [batchingContextID]: fetchRelationships
        }),
        invoked: true
      });
      cb.setLoadingState(true);
      await config.bind(path, data => {
        logVerbose(`Data bound (${ref})`);
        if (!accumulatorID || !writingCallbacks) return;
        const active = activeBindingsByPath[path];
        if (!active) return;
        const bindIsInitialized = (active === null || active === void 0 ? void 0 : active.value) !== undefined;
        item = data;
        active.value = data;

        if (!bindIsInitialized) {
          activeBindingsByPath[path].value = data;
          item = maybeHydrateItemFromCache(data, fetchRelationships, logVerbose);
          cb.writerCallback(item, true);
        } else {
          logVerbose('Checking for added child relationships');
          /**
           * Indicates subsequent run, fetch relationships with each contextual
           * relationship structure again in case there are any added child
           * relationships. Since this is a bind response, the value is already
           * fetched and will avert a rebind and provide the value cached.
           */

          Object.entries(active.relationshipsByContext).forEach(([contextID, relationships]) => {
            const writersForContext = writeCallbacksByContext[contextID];
            /**
             * If no writers are found for the given context, bind responses
             * for this context have nowhere to go and can be ignored.
             */

            if (!writersForContext) return;
            fetchWithRelationships(ref, entityID, relationships, Object.assign(Object.assign({}, options), {
              batchingContextID: contextID,
              writers: writersForContext
            }), {
              batchingTimeout: 20,
              depth: depth + 1,
              parentPath,
              prevAccumulatorID: accumulatorID,
              rootRef
            });
          });
        }
      });
    } else {
      cb.setLoadingState(true);
      item = (_h = activeBindingsByPath[path]) === null || _h === void 0 ? void 0 : _h.value;

      if (!item) {
        item = await config.getValueOnce(path);
        logVerbose(`Data fetched once (${ref})`);
      }

      if (!item) {
        logVerbose(`No item found (${ref})`);
        return (_j = accumulators[accumulatorID]) !== null && _j !== void 0 ? _j : {};
      }

      cb.writerCallback(item, activeBindIsInitialized);
    }
  } catch (error) {
    FWRError(error);
    return (_k = accumulators[accumulatorID]) !== null && _k !== void 0 ? _k : {};
  } finally {
    cb.setLoadingState(false);
  }

  if (!item) {
    FWRError('Item not found', ref, entityID);
    return (_l = accumulators[accumulatorID]) !== null && _l !== void 0 ? _l : {};
  }
  /** Add resulting item to accumulator for response */


  accumulators[accumulatorID] = Object.assign(Object.assign({}, accumulators[accumulatorID]), {
    [ref]: Object.assign(Object.assign({}, (_m = accumulators[accumulatorID]) === null || _m === void 0 ? void 0 : _m[ref]), {
      [entityID]: item
    })
  }); // Log error and return only base data without writer callback if
  // `fetchRelationships` falsy.

  if (!fetchRelationships) {
    FWRError('No intended relationships found', ref);
    return getResponseAndCleanup(accumulatorID, batchingContextID, prevAccumulatorID);
  } // Internally, `true` is used for children that are to be fetched without
  // relationships. Run the writer callback and return the child data.


  if (fetchRelationships === true) {
    cb.writerCallback(item, activeBindIsInitialized);
    return (_o = accumulators[accumulatorID]) !== null && _o !== void 0 ? _o : {};
  }

  const refRelationshipKeys = dbRefsWithRelationshipKeys[ref];
  let requiresRewrite = false;
  const childPromises = Object.entries(fetchRelationships).map(async relationship => {
    var _a;

    if (!item) return;
    const [itemChildKey, childRequest] = relationship;
    /** Typecasting to RelationshipKey | undefined is safe */

    const childRelationshipKey = (_a = refRelationshipKeys) === null || _a === void 0 ? void 0 : _a[itemChildKey];
    if (!childRelationshipKey) return;
    let itemChild = item[itemChildKey];
    if (!itemChild) return;
    const {
      childIDs,
      itemChild: newItemChild,
      wasDecrypted
    } = await getChildFetchIDs(item, itemChildKey, cb.setLoadingState);
    if (!childIDs.length) return;
    /**
     * If the child item was decrypted, we need to clone it to replace the original
     * itemChild with the decrypted item.
     */

    if (wasDecrypted) {
      item[itemChildKey] = cloneDeep(newItemChild);
      itemChild = cloneDeep(newItemChild);
      requiresRewrite = true;
    }

    const childDbRef = childRelationshipKey.ref;
    const childFetchPromises = childIDs.map(childID => fetchWithRelationships(
    /** Typecast to RelationshipKeySupportedDbRefs is safe since this will be validated */
    childDbRef, childID, childRequest, Object.assign(Object.assign({}, options), {
      batchingContextID,
      writers: writingCallbacks
    }), {
      batchingTimeout,
      depth: depth + 1,
      parentPath: path,
      prevAccumulatorID: accumulatorID,
      rootRef
    }));
    await Promise.all(childFetchPromises);
  });
  await Promise.all(childPromises);
  /** If a child key requires a rewrite, we need to provide the item to the writer callback */

  if (requiresRewrite) {
    cb.writerCallback(item, activeBindIsInitialized);
  }
  /** Finishing logic goes here */


  if (isRootCall) {
    logVerbose('Finishing root call');
    const timestamps = cloneDeep(accumulatorsTimestamps);
    const finalTime = Number((_p = timestamps[accumulatorID]) !== null && _p !== void 0 ? _p : 0);
    const totalTime = Date.now() - finalTime;
    const finalWrites = Object.entries((_q = batches[batchingContextID]) !== null && _q !== void 0 ? _q : {}).map(async ([batchRef, batch]) => {
      if (!accumulatorID) return;
      const promises = Object.entries(batch.response).map(async ([childID, child]) => {
        var _a;

        if (!accumulatorID) return;
        const childItem = batchRef === 'loadingIDs' ? false : child;
        /** Add resulting item to accumulator for response */

        accumulators[accumulatorID] = Object.assign(Object.assign({}, accumulators[accumulatorID]), {
          [batchRef]: Object.assign(Object.assign({}, (_a = accumulators[accumulatorID]) === null || _a === void 0 ? void 0 : _a[batchRef]), {
            [childID]: childItem
          })
        });
        return batchedWriter({
          newItem: childItem,
          id: childID,
          ref: batchRef,
          bindResponse: false,
          accumulatorID,
          batchingContextID,
          writers,
          timeout: 10
        }, logVerbose);
      });
      await Promise.all(promises);
    });
    await Promise.all(finalWrites);

    const formatTimestamps = batchInfo => {
      const formatTimestamp = info => {
        const diff = info.batchTimestamps - finalTime;
        return Math.round(diff / 10) / 100;
      };

      const res = {};
      batchInfo.forEach((info, i) => {
        res[`B-${i + 1} count`] = info.batchCount;
        res[`B-${i + 1} time`] = formatTimestamp(info);
      });
      return res;
    };

    const batchInfo = objectMap((_r = batchesInfo[batchingContextID]) !== null && _r !== void 0 ? _r : {}, batch => formatTimestamps(batch));

    if (Object.keys(batchInfo).length) {
      delete batchInfo.loadingIDs;
      const totalSeconds = (totalTime / 1000).toFixed(2);
      console.info(`[FWR]: Total time for ${batchingContextID}: ${totalSeconds}s (${totalTime}ms)`);
      logBindings();
    }

    logVerbose('Returning response from root call');
  } else {
    logVerbose(`Finishing child call (${ref})`);
  }

  return getResponseAndCleanup(accumulatorID, batchingContextID, prevAccumulatorID);
};
/**
 * Unbinds all relationships for a given context ID.
 * If the context provided for a given data item is the only one remaining,
 * the item is properly unbound and cleaned up.
 *
 * @param contextID The ID of the context to unbind.
 */

const unbindByContext = contextID => {
  Object.keys(activeBindingsByPath).forEach(path => {
    var _a, _b, _c, _d;

    if (!(config === null || config === void 0 ? void 0 : config.unbind)) return;
    (_b = (_a = activeBindingsByPath[path]) === null || _a === void 0 ? void 0 : _a.relationshipsByContext) === null || _b === void 0 ? true : delete _b[contextID];

    if (Object.keys((_d = (_c = activeBindingsByPath[path]) === null || _c === void 0 ? void 0 : _c.relationshipsByContext) !== null && _d !== void 0 ? _d : {}).length === 0) {
      delete activeBindingsByPath[path];
      config.unbind(path);
    }
  });
  delete writeCallbacksByContext[contextID];
};

export { fetchWithRelationships, getBindingsReport, initFetchWithRelationshipsConfig, logBindings, recursiveDecrypt, setFWRLoggingMeta, unbindByContext };
